import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Activity } from "lucide-react";
import { PageHeader, Loading, Empty } from "@/components/Common";
import { EUR, EURc, num, pnlColor, fmtTime, dur } from "@/lib/format";
import api from "@/lib/api";

function DirBadge({ d }) {
  const buy = d === "BUY";
  return (
    <span className="rounded px-2 py-0.5 font-mono text-[10px] font-semibold tracking-wider"
      style={{ color: buy ? "#10B981" : "#EF4444", background: buy ? "rgba(16,185,129,.1)" : "rgba(239,68,68,.1)" }}>
      {d}
    </span>
  );
}

export default function LivePositions() {
  const { data, isLoading } = useQuery({
    queryKey: ["positions"],
    queryFn: () => api.get("/positions").then((r) => r.data),
    refetchInterval: 8000,
  });

  if (isLoading || !data) return <Loading />;
  const pos = data.positions || [];

  return (
    <div data-testid="live-positions-page">
      <PageHeader title="Live Positions" subtitle="Posizioni aperte — aggiornamento quasi in tempo reale (solo lettura)">
        <div className="rounded-full border border-gold/20 bg-gold/5 px-4 py-1.5 font-mono text-sm">
          <span className="text-zinc-400">Floating: </span>
          <span className={pnlColor(data.total_floating)}>{EURc(data.total_floating)}</span>
        </div>
      </PageHeader>

      {pos.length === 0 ? (
        <Empty label="Nessuna posizione aperta al momento" />
      ) : (
        <>
          {/* Mobile cards */}
          <div className="space-y-3 lg:hidden">
            {pos.map((p, i) => (
              <motion.div key={p.ticket} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                className="dlx-card p-4" data-testid={`position-card-${p.ticket}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-sans font-semibold text-white">{p.symbol}</span>
                    <DirBadge d={p.direction} />
                  </div>
                  <span className={`font-mono text-lg font-bold ${pnlColor(p.profit)}`}>{EURc(p.profit)}</span>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2 font-mono text-xs text-zinc-400">
                  <div><div className="text-[9px] uppercase tracking-wider text-zinc-600">Vol</div>{p.volume}</div>
                  <div><div className="text-[9px] uppercase tracking-wider text-zinc-600">Entry</div>{num(p.entry_price, 2)}</div>
                  <div><div className="text-[9px] uppercase tracking-wider text-zinc-600">Current</div>{num(p.current_price, 2)}</div>
                  <div><div className="text-[9px] uppercase tracking-wider text-zinc-600">SL</div>{num(p.stop_loss, 2)}</div>
                  <div><div className="text-[9px] uppercase tracking-wider text-zinc-600">TP</div>{num(p.take_profit, 2)}</div>
                  <div><div className="text-[9px] uppercase tracking-wider text-zinc-600">Durata</div>{fmtTime(p.open_time)}</div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="dlx-card hidden overflow-hidden lg:block" data-testid="live-positions-table">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/8 text-left">
                  {["Ticket", "Simbolo", "Dir", "Volume", "Entry", "Current", "SL", "TP", "Apertura", "P&L"].map((h) => (
                    <th key={h} className="px-4 py-3.5 font-sans text-[10px] font-medium uppercase tracking-widest text-zinc-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pos.map((p) => (
                  <tr key={p.ticket} className="border-b border-white/5 transition-colors hover:bg-white/[0.02]" data-testid={`position-row-${p.ticket}`}>
                    <td className="px-4 py-3.5 font-mono text-xs text-zinc-500">#{p.ticket}</td>
                    <td className="px-4 py-3.5 font-sans text-sm font-medium text-white">{p.symbol}</td>
                    <td className="px-4 py-3.5"><DirBadge d={p.direction} /></td>
                    <td className="px-4 py-3.5 font-mono text-sm text-zinc-300">{p.volume}</td>
                    <td className="px-4 py-3.5 font-mono text-sm text-zinc-300">{num(p.entry_price, 2)}</td>
                    <td className="px-4 py-3.5 font-mono text-sm text-white">{num(p.current_price, 2)}</td>
                    <td className="px-4 py-3.5 font-mono text-sm text-zinc-500">{num(p.stop_loss, 2)}</td>
                    <td className="px-4 py-3.5 font-mono text-sm text-zinc-500">{num(p.take_profit, 2)}</td>
                    <td className="px-4 py-3.5 font-mono text-xs text-zinc-500">{fmtTime(p.open_time)}</td>
                    <td className={`px-4 py-3.5 font-mono text-sm font-semibold ${pnlColor(p.profit)}`}>{EURc(p.profit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
